import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Shield, Users, ShoppingBag, Check, X, Clock, Eye, ChevronDown, ChevronUp, Power, ToggleLeft, ToggleRight, Linkedin, Phone, CreditCard, DollarSign, Shuffle, ArrowRight as ArrowRightIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('approvals');
  const [processing, setProcessing] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const [marketOpen, setMarketOpen] = useState(true);
  const [togglingMarket, setTogglingMarket] = useState(false);
  const [marketReopensAt, setMarketReopensAt] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [payments, setPayments] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [swappingPayment, setSwappingPayment] = useState(null);
  const [swapResult, setSwapResult] = useState(null); // { payment_id, swaps: [...] }
  const countdownRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, usersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/pending-approvals`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/payments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setPendingApprovals(pendingRes.data || []);
      setUsers(usersRes.data || []);
      setPayments(paymentsRes.data || []);
      // Fetch market settings
      try {
        const settingsRes = await axios.get(`${API}/settings`);
        setMarketOpen(settingsRes.data.market_open !== false);
        setMarketReopensAt(settingsRes.data.market_reopens_at || null);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/approve/${userId}`, {}, { withCredentials: true });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/reject/${userId}`, {}, { withCredentials: true });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const handlePaymentAction = async (paymentId, status) => {
    setProcessingPayment(paymentId);
    try {
      await axios.put(`${API}/admin/payments/${paymentId}`, { status }, { withCredentials: true });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleSwapStudents = async (paymentId) => {
    if (!window.confirm('Are you sure you want to swap all students for this payment? This cannot be undone.')) return;
    setSwappingPayment(paymentId);
    try {
      const res = await axios.post(`${API}/admin/payments/${paymentId}/swap`, {}, { withCredentials: true });
      setSwapResult(res.data);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Swap failed');
    } finally {
      setSwappingPayment(null);
    }
  };

  const getFileUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND}/api/files/${path}`;
  };

  // Countdown timer for admin view
  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (!marketReopensAt) {
      setCountdown(null);
      return;
    }
    const reopensTime = new Date(marketReopensAt).getTime();
    const tick = () => {
      const diff = reopensTime - Date.now();
      if (diff <= 0) {
        setCountdown(null);
        setMarketReopensAt(null);
        if (countdownRef.current) clearInterval(countdownRef.current);
        return;
      }
      setCountdown({
        mins: Math.floor(diff / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [marketReopensAt]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#121212] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8" strokeWidth={2.5} />
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-dashboard-title">
              Admin
            </h1>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Manage the market</p>
          </div>
        </div>

        {/* Market Toggle */}
        <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5 mb-8" data-testid="market-toggle-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Power className="w-6 h-6" strokeWidth={2.5} style={{ color: marketOpen ? '#16a34a' : '#FF6B6B' }} />
              <div>
                <div className="font-black uppercase text-base tracking-tight">Market Status</div>
                <div className="text-xs font-medium" style={{ color: '#888' }}>
                  {!marketOpen
                    ? 'The market is napping — chart is replaced with a pause screen'
                    : countdown
                      ? 'Market toggled ON — 30-min countdown is running for users'
                      : 'The market is live — chart is visible to all users'
                  }
                </div>
              </div>
            </div>
            <button
              data-testid="toggle-market-btn"
              disabled={togglingMarket}
              onClick={async () => {
                setTogglingMarket(true);
                try {
                  const res = await axios.put(`${API}/admin/settings`, { market_open: !marketOpen }, { withCredentials: true });
                  setMarketOpen(res.data.market_open !== false);
                  setMarketReopensAt(res.data.market_reopens_at || null);
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to toggle market');
                } finally {
                  setTogglingMarket(false);
                }
              }}
              className={`flex items-center gap-2 font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-5 py-2.5 transition-all disabled:opacity-50 ${
                marketOpen
                  ? 'bg-[#6EE7B7] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212]'
                  : 'bg-[#FF6B6B] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212]'
              }`}
            >
              {marketOpen ? <ToggleRight className="w-5 h-5" strokeWidth={2.5} /> : <ToggleLeft className="w-5 h-5" strokeWidth={2.5} />}
              {togglingMarket ? 'Switching...' : marketOpen ? 'Market On' : 'Market Off'}
            </button>
          </div>
          {/* Countdown indicator */}
          {countdown && (
            <div className="mt-4 bg-[#121212] border-[2px] border-[#E8C86A] rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-wider" style={{ color: '#E8C86A' }}>Countdown Active</div>
                <div className="text-xs font-medium mt-0.5" style={{ color: '#999' }}>Users see a countdown timer until the market opens</div>
              </div>
              <div className="font-mono text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#E8C86A' }} data-testid="admin-countdown">
                {String(countdown.mins).padStart(2, '0')}:{String(countdown.secs).padStart(2, '0')}
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Pending Review', value: stats.pending_approvals || 0, color: '#FF6B6B' },
            { label: 'Approved Students', value: stats.approved_wearers || 0, color: '#6EE7B7' },
            { label: 'Active Asks', value: stats.active_bids || 0, color: '#E8C86A' },
            { label: 'Acceptances', value: stats.total_acceptances || 0, color: '#D8DFEA' },
          ].map((s, i) => (
            <div key={i} className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-4">
              <div className="font-mono text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</div>
              <div className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'approvals', label: `Pending Review (${pendingApprovals.length})`, icon: Clock },
            { key: 'payments', label: `Payments (${payments.length})`, icon: CreditCard },
            { key: 'users', label: `All Users (${users.length})`, icon: Users },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-sm font-black uppercase tracking-wider border-[3px] border-[#121212] rounded-xl px-4 py-2.5 transition-all flex items-center gap-2 ${
                tab === t.key ? 'bg-[#121212] text-[#F6F1E8]' : 'bg-[#EDE3C7] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px]'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" strokeWidth={2.5} /> {t.label}
            </button>
          ))}
        </div>

        {/* Pending Approvals Tab */}
        {tab === 'approvals' && (
          <div>
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-16 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl">
                <Check className="w-12 h-12 mx-auto mb-4" strokeWidth={1.5} style={{ color: '#6EE7B7' }} />
                <p className="text-xl font-black uppercase mb-2">All caught up!</p>
                <p className="text-sm font-medium" style={{ color: '#888' }}>No pending approvals right now.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingApprovals.map(u => (
                  <div
                    key={u.user_id}
                    className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl overflow-hidden"
                    data-testid={`approval-${u.user_id}`}
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl border-[3px] border-[#121212] flex items-center justify-center font-black text-lg" style={{ backgroundColor: '#F6F1E8' }}>
                            {(u.name || u.email || 'W')[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-base">{u.name || 'Anonymous'}</div>
                            <div className="text-xs font-medium" style={{ color: '#888' }}>{u.email}</div>
                            {u.city && <div className="text-xs font-medium" style={{ color: '#888' }}>{u.city}</div>}
                            {u.phone_number && (
                              <div className="flex items-center gap-1.5 text-xs font-medium mt-0.5" style={{ color: '#888' }}>
                                <Phone className="w-3 h-3" strokeWidth={2.5} />
                                {u.phone_number}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            data-testid={`approve-${u.user_id}`}
                            onClick={() => handleApprove(u.user_id)}
                            disabled={processing === u.user_id}
                            className="bg-[#6EE7B7] text-[#121212] font-black uppercase text-xs border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2 flex items-center gap-1 disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" strokeWidth={3} /> Approve
                          </button>
                          <button
                            data-testid={`reject-${u.user_id}`}
                            onClick={() => handleReject(u.user_id)}
                            disabled={processing === u.user_id}
                            className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-xs border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2 flex items-center gap-1 disabled:opacity-50"
                          >
                            <X className="w-4 h-4" strokeWidth={3} /> Reject
                          </button>
                        </div>
                      </div>

                      {/* Social & Contact Info */}
                      {(u.linkedin_url || u.twitter_handle || u.phone_number) && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {u.linkedin_url && (
                            <a
                              href={u.linkedin_url.startsWith('http') ? u.linkedin_url : `https://${u.linkedin_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs font-bold border-[2px] border-[#121212] rounded-lg px-3 py-1.5 bg-[#F6F1E8] hover:bg-[#0077B5] hover:text-white hover:border-[#0077B5] transition-all"
                              data-testid={`linkedin-${u.user_id}`}
                            >
                              <Linkedin className="w-3.5 h-3.5" strokeWidth={2.5} />
                              LinkedIn
                            </a>
                          )}
                          {u.twitter_handle && (
                            <a
                              href={`https://x.com/${u.twitter_handle.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs font-bold border-[2px] border-[#121212] rounded-lg px-3 py-1.5 bg-[#F6F1E8] hover:bg-[#121212] hover:text-white transition-all"
                              data-testid={`twitter-${u.user_id}`}
                            >
                              <span className="font-black">𝕏</span>
                              @{u.twitter_handle.replace('@', '')}
                            </a>
                          )}
                          {u.phone_number && (
                            <a
                              href={`tel:${u.phone_number}`}
                              className="flex items-center gap-1.5 text-xs font-bold border-[2px] border-[#121212] rounded-lg px-3 py-1.5 bg-[#F6F1E8] hover:bg-[#6EE7B7] hover:border-[#16a34a] transition-all"
                              data-testid={`phone-${u.user_id}`}
                            >
                              <Phone className="w-3.5 h-3.5" strokeWidth={2.5} />
                              {u.phone_number}
                            </a>
                          )}
                        </div>
                      )}

                      {/* YC Screenshot */}
                      {u.yc_screenshot_url && (
                        <div className="mt-4">
                          <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>
                            YC Acceptance Screenshot
                          </div>
                          <div
                            className="border-[2px] border-[#121212] rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setExpandedImage(expandedImage === u.user_id ? null : u.user_id)}
                            data-testid={`screenshot-${u.user_id}`}
                          >
                            <img
                              src={getFileUrl(u.yc_screenshot_url)}
                              alt="YC Screenshot"
                              className={`w-full object-contain ${expandedImage === u.user_id ? 'max-h-[600px]' : 'max-h-48'} transition-all`}
                            />
                          </div>
                          <button
                            onClick={() => setExpandedImage(expandedImage === u.user_id ? null : u.user_id)}
                            className="text-xs font-bold mt-1 flex items-center gap-1 hover:text-[#FF6B6B]"
                            style={{ color: '#888' }}
                          >
                            <Eye className="w-3 h-3" strokeWidth={2.5} />
                            {expandedImage === u.user_id ? 'Collapse' : 'Expand image'}
                          </button>
                        </div>
                      )}
                      {!u.yc_screenshot_url && (
                        <div className="mt-4 bg-[#FF6B6B]/10 border-[2px] border-[#FF6B6B] rounded-xl p-3 text-xs font-bold" style={{ color: '#FF6B6B' }}>
                          No YC screenshot uploaded
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {tab === 'payments' && (
          <div>
            {payments.length === 0 ? (
              <div className="text-center py-16 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl">
                <CreditCard className="w-12 h-12 mx-auto mb-4" strokeWidth={1.5} style={{ color: '#aaa' }} />
                <p className="text-xl font-black uppercase mb-2">No Payments Yet</p>
                <p className="text-sm font-medium" style={{ color: '#888' }}>Payment records will appear here when startups check out.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {payments.map(p => (
                  <div
                    key={p.payment_id}
                    className={`border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl overflow-hidden ${
                      p.status === 'completed' ? 'bg-[#6EE7B7]/20' : p.status === 'rejected' ? 'bg-[#FF6B6B]/10' : 'bg-[#EDE3C7]'
                    }`}
                    data-testid={`payment-${p.payment_id}`}
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-base">{p.startup_name || 'Unknown Startup'}</span>
                            <span className={`text-xs font-black uppercase border-[2px] border-[#121212] rounded-full px-2.5 py-0.5 ${
                              p.status === 'completed' ? 'bg-[#6EE7B7]' : p.status === 'rejected' ? 'bg-[#FF6B6B]' : 'bg-[#E8C86A]'
                            }`}>
                              {p.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs font-medium" style={{ color: '#888' }}>
                            <span className="font-mono font-bold text-base" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#121212' }}>
                              ${p.amount}
                            </span>
                            <span>via <span className="font-bold uppercase">{p.payment_method}</span></span>
                            <span>·</span>
                            <span>{p.acceptance_count} student{p.acceptance_count !== 1 ? 's' : ''}</span>
                            <span>·</span>
                            <span>{new Date(p.created_at).toLocaleDateString()} {new Date(p.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                        {p.status === 'pending' && (
                          <div className="flex gap-2 flex-shrink-0">
                            {!p.swapped && (
                              <button
                                onClick={() => handleSwapStudents(p.payment_id)}
                                disabled={swappingPayment === p.payment_id}
                                className="bg-[#C4B5FD] text-[#121212] font-black uppercase text-xs border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2 flex items-center gap-1 disabled:opacity-50"
                                data-testid={`swap-students-${p.payment_id}`}
                              >
                                <Shuffle className="w-4 h-4" strokeWidth={3} /> {swappingPayment === p.payment_id ? 'Swapping...' : 'Swap'}
                              </button>
                            )}
                            {p.swapped && (
                              <span className="bg-[#C4B5FD] text-[#121212] text-xs font-black uppercase border-[2px] border-[#121212] rounded-full px-2.5 py-1.5 flex items-center gap-1">
                                <Shuffle className="w-3 h-3" strokeWidth={3} /> Swapped
                              </span>
                            )}
                            <button
                              onClick={() => handlePaymentAction(p.payment_id, 'completed')}
                              disabled={processingPayment === p.payment_id}
                              className="bg-[#6EE7B7] text-[#121212] font-black uppercase text-xs border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2 flex items-center gap-1 disabled:opacity-50"
                              data-testid={`confirm-payment-${p.payment_id}`}
                            >
                              <Check className="w-4 h-4" strokeWidth={3} /> Confirm
                            </button>
                            <button
                              onClick={() => handlePaymentAction(p.payment_id, 'rejected')}
                              disabled={processingPayment === p.payment_id}
                              className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-xs border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2 flex items-center gap-1 disabled:opacity-50"
                              data-testid={`reject-payment-${p.payment_id}`}
                            >
                              <X className="w-4 h-4" strokeWidth={3} /> Reject
                            </button>
                          </div>
                        )}
                        {p.status !== 'pending' && p.reviewed_at && (
                          <div className="text-xs font-medium text-right" style={{ color: '#888' }}>
                            <div>Reviewed</div>
                            <div>{new Date(p.reviewed_at).toLocaleDateString()}</div>
                          </div>
                        )}
                      </div>

                      {/* Student Details */}
                      {p.students && p.students.length > 0 && (
                        <div className="mt-4 border-t-[2px] border-[#121212]/10 pt-3">
                          <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>
                            Students ({p.students.length})
                          </div>
                          <div className="space-y-2">
                            {p.students.map((s, i) => (
                              <div key={i} className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-3 py-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold text-sm truncate">{s.pseudonym}</div>
                                  <div className="text-xs font-medium truncate" style={{ color: '#888' }}>
                                    {s.swapped ? (
                                      <>
                                        <span style={{ textDecoration: 'line-through', color: '#bbb' }}>{s.original_name || 'N/A'} · {s.original_email || 'N/A'}</span>
                                        <span style={{ color: '#7C3AED' }}> → {s.name || 'N/A'} · {s.email || 'N/A'}</span>
                                      </>
                                    ) : (
                                      <>{s.name || 'N/A'} · {s.email || 'N/A'}</>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0 ml-3">
                                  <div className="font-mono text-sm font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${s.asking_price}</div>
                                  <div className={`text-xs font-black uppercase ${s.status === 'confirmed' ? 'text-[#16a34a]' : 'text-[#E8C86A]'}`}>
                                    {s.status === 'confirmed' ? 'paid' : s.status}
                                  </div>
                                  {s.swapped && (
                                    <div className="text-xs font-bold mt-1" style={{ color: '#7C3AED' }}>
                                      <Shuffle className="w-3 h-3 inline mr-0.5" strokeWidth={2.5} />
                                      swapped
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Users Tab */}
        {tab === 'users' && (
          <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-[3px] border-[#121212]">
                    <th className="text-left px-5 py-3 text-xs font-black uppercase tracking-wider">User</th>
                    <th className="text-left px-5 py-3 text-xs font-black uppercase tracking-wider">Role</th>
                    <th className="text-left px-5 py-3 text-xs font-black uppercase tracking-wider">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-black uppercase tracking-wider">City</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} className="border-b border-[#121212]/10" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-5 py-3">
                        <div className="font-bold text-sm">{u.name || 'N/A'}</div>
                        <div className="text-xs font-medium" style={{ color: '#888' }}>{u.email}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-black uppercase border-[2px] border-[#121212] rounded-full px-2.5 py-0.5 ${
                          u.role === 'admin' ? 'bg-[#D8DFEA]' : u.role === 'startup' ? 'bg-[#E8C86A]' : 'bg-[#FF6B6B]'
                        }`}>
                          {u.role || 'none'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {u.role === 'wearer' && (
                          <span className={`text-xs font-black uppercase ${
                            u.approval_status === 'approved' ? 'text-[#16a34a]' :
                            u.approval_status === 'rejected' ? 'text-[#FF6B6B]' :
                            u.approval_status === 'pending' ? 'text-[#E8C86A]' : ''
                          }`}>
                            {u.approval_status || 'n/a'}
                          </span>
                        )}
                        {u.role !== 'wearer' && <span className="text-xs" style={{ color: '#888' }}>-</span>}
                      </td>
                      <td className="px-5 py-3 text-sm font-medium" style={{ color: '#888' }}>{u.city || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Expanded image overlay */}
      {expandedImage && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setExpandedImage(null)}>
          <div className="max-w-3xl max-h-[80vh] overflow-auto">
            {pendingApprovals.filter(u => u.user_id === expandedImage).map(u => (
              <img
                key={u.user_id}
                src={getFileUrl(u.yc_screenshot_url)}
                alt="YC Screenshot"
                className="rounded-2xl border-[3px] border-[#121212]"
                onClick={e => e.stopPropagation()}
              />
            ))}
          </div>
        </div>
      )}

      {/* Swap Result Modal */}
      {swapResult && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSwapResult(null)}>
          <div className="bg-[#F6F1E8] border-[3px] border-[#121212] shadow-[8px_8px_0px_0px_#121212] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-[#C4B5FD] border-[3px] border-[#121212] rounded-xl flex items-center justify-center">
                  <Shuffle className="w-5 h-5" strokeWidth={3} />
                </div>
                <div>
                  <h3 className="font-black uppercase text-lg">Swap Complete</h3>
                  <p className="text-xs font-medium" style={{ color: '#888' }}>
                    {swapResult.count} swapped · {swapResult.kept_count || 0} kept (allow list)
                  </p>
                </div>
              </div>

              {swapResult.message && (
                <div className="bg-[#6EE7B7]/20 border-[2px] border-[#6EE7B7] rounded-xl p-3 mb-4 text-sm font-bold text-center">
                  {swapResult.message}
                </div>
              )}

              {/* Kept students (allow list) */}
              {swapResult.kept && swapResult.kept.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#16a34a' }}>
                    ✓ Kept — Allow List ({swapResult.kept.length})
                  </div>
                  <div className="space-y-2">
                    {swapResult.kept.map((k, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#6EE7B7]/10 border-[2px] border-[#6EE7B7]/30 rounded-xl px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{k.pseudonym}</div>
                          <div className="text-xs font-medium truncate" style={{ color: '#888' }}>
                            {k.name || k.name?.name || 'N/A'} · {k.email || k.name?.email || 'N/A'}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs font-black uppercase" style={{ color: '#16a34a' }}>
                          <Check className="w-3.5 h-3.5" strokeWidth={3} /> Kept
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Swapped students */}
              {swapResult.swaps && swapResult.swaps.length > 0 && (
                <div>
                  <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#7C3AED' }}>
                    ↻ Swapped ({swapResult.swaps.length})
                  </div>
                  <div className="space-y-3">
                    {swapResult.swaps.map((s, i) => (
                      <div key={i} className="bg-[#EDE3C7] border-[2px] border-[#121212] rounded-xl p-4">
                        <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#7C3AED' }}>{s.pseudonym}</div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-[#FF6B6B]/10 border-[2px] border-[#FF6B6B]/30 rounded-lg p-2">
                            <div className="text-xs font-black uppercase mb-0.5" style={{ color: '#FF6B6B' }}>Original</div>
                            <div className="text-sm font-bold truncate">{s.original_name}</div>
                            <div className="text-xs font-medium truncate" style={{ color: '#888' }}>{s.original_email}</div>
                          </div>
                          <ArrowRightIcon className="w-5 h-5 flex-shrink-0" strokeWidth={3} style={{ color: '#7C3AED' }} />
                          <div className="flex-1 bg-[#6EE7B7]/10 border-[2px] border-[#6EE7B7]/30 rounded-lg p-2">
                            <div className="text-xs font-black uppercase mb-0.5" style={{ color: '#16a34a' }}>Swapped To</div>
                            <div className="text-sm font-bold truncate">{s.swapped_name}</div>
                            <div className="text-xs font-medium truncate" style={{ color: '#888' }}>{s.swapped_email}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setSwapResult(null)}
                className="w-full mt-5 bg-[#121212] text-[#F6F1E8] font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-6 py-3 hover:opacity-90 transition-opacity"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
